import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  useOrganizationService,
  useProjectService,
  useResourcesService,
  useRoadmapService,
  useTemplateService,
  useIntegrationService,
} from "../../services";
import { useDispatch, useSelector } from "react-redux";
import { useSideNavContext } from "../../context/SideNavContext";
import CreateRoadMapsModal from "./CreateRoadMapsModal";
import CreateAdvancedEpicModal from "../../features/AdvancedEpic/CreateAdvancedEpicModal";
import RoadmapSidebar from "./RoadmapSidebar";
import "react-timelines/lib/css/style.css";
import BreadcrumbHeader, { BreadcrumbItem } from "./BreadcrumbHeader";
import TopNavBar from "./TopNavBar";
import TimelineNavBar from "./TimelineNavBar";
import "./styles.css";
import { ViewType } from "../Timeline/types";
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from "react-resizable-panels";
import {
  ENTITY,
  resetDashboardDetails,
  resetProjectIdForScroll,
  resetProjectName,
  resetSelectedTaskId,
  resetTaskIdForScroll,
  resetTimelineState,
  setActiveTab,
  setCurrentEntity,
  setDashboardDetails,
  setProjectIdForScroll,
  setProjectName,
  setProjectPeriod,
  setRoadmapName,
  setRoadmapPeriod,
  setSimulation,
  setTaskIdForScroll,
  setViewType,
} from "../../redux/slices/timeline";
import GanttViewTimeline from "../Timeline";
import BirdsEyeView from "../Timeline/components/BirdsEyeView";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import moment from "moment";
import TaskCompletionUpdateModal from "../Projects/sections/TaskTableModal";
import ListViewTable from "./RoadmapViewTable";
import { useJwt } from "../../helpers/common";
import AddUpdateProjectModal from "../Projects/sections/AddUpdateProjectModal";
import ViewHistoryModal from "../Projects/sections/CPLIComponent/ViewHistoryModal";
import EmailNotificationModal from "../Projects/sections/CPLIComponent/EmailNotificationModal";
import SkillsModal from "./SkillsModal";
import AddUpdateTemplate from "../Templates/AddUpdateTemplate";
import { resetTemplateId, setTemplateId } from "../../redux/slices/templates";
import {
  ChildEntityTypeConfig,
  DashboardStatus,
} from "../../constants/integrationTypes";
import ProjectDetailsModal from "./ProjectDetailsModal";
import useDashboardService from "../../services/dashboard";
import CreateTaskWarningModal from "./modals/CreateTaskWarningModal";
import MergeWarningModal from "./modals/MergeWarningModal";
import ResetWarningModal from "./modals/ResetWarningModal";
import VersionHistoryModal from "./modals/VersionHistoryModal";
import ShareModal from "./modals/ShareModal";
import SyncModal from "./modals/SyncModal";
import SyncCompletionToast from "./components/SyncCompletionToast";
import LoadingState from "../Timeline/components/LoadingState";
import EmptyState from "../Timeline/components/EmptyState";
import { resetRoadmap, setSingleRoadmap } from "../../redux/slices/roadmap";
import ProjectListView from "./ProjectListView";
import { useDraggable } from "react-use-draggable-scroll";
import ProjectEmptyStateModal from "./ProjectEmptyStateModal";
import Alert from "../../shared/atoms/Alert";

interface DashboardDetailsProps {
  projectId?: string;
  taskData?: any;
  readOnly?: boolean;
  onBack?: () => void;
}

const DashboardDetails: React.FC<DashboardDetailsProps> = ({
  projectId,
  taskData = {},
  readOnly = false,
  onBack,
}) => {
  const dispatch = useDispatch();
  const { roadmap_id, project_id = projectId, template_id } = useParams();
  const effectiveProjectId = template_id || project_id;
  const { fetchProjectDashboard } = useDashboardService();
  const { fetchSingleRoadmap, fetchData, updateRoadmap } = useRoadmapService();
  const {
    resyncEpicWithStatus,
    setSyncStatusCallback: setIntegrationSyncStatusCallback,
    setSyncCompletionCallback: setIntegrationSyncCompletionCallback,
    getResyncStatus,
  } = useIntegrationService();
  const { fetchManagerResources } = useResourcesService();
  const { fetchOrgSchedule } = useOrganizationService();
  const {
    fetchProjectVersion,
    fetchProjectIntegrations,
    fetchProjectShareTokens,
    deleteProjects,
    updateProject,
  } = useProjectService();
  const { getTemplateById } = useTemplateService();
  const { isOpenOnSmallScreens } = useSideNavContext();
  const navigate = useNavigate();
  const location = useLocation();
  const jwtData: any = useJwt();

  const [showRoadMapModal, setShowRoadMapModal] = useState(false);
  const [shouldScrollToTabs, setShouldScrollToTabs] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const sidebarPanelRef = useRef<ImperativePanelHandle>(null);
  const ref =
    useRef<HTMLDivElement>() as React.MutableRefObject<HTMLInputElement>;
  const { events } = useDraggable(ref); // Now we pass the reference to the useDraggable hook:

  // Project-specific state variables
  const [showCreateTaskWarning, setShowCreateTaskWarning] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [showMergeWarningModal, setShowMergeWarningModal] = useState(false);
  const [showResetWarningModal, setShowResetWarningModal] = useState(false);
  const [showVersionHistoryModal, setShowVersionHistoryModal] = useState(false);
  const [showProjectHistoryModal, setShowProjectHistoryModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [selectedTaskForEdit, setSelectedTaskForEdit] = useState(null);
  const [showSkillsModal, setShowSkillsModal] = useState(false);
  const [showEditTemplateModal, setShowEditTemplateModal] = useState(false);
  const [templatePayloadData, setTemplatePayloadData] = useState({});

  // New state variables for Share, Sync Tickets, and Notifications
  const [showShareModal, setShowShareModal] = useState(false);
  const [showEmailNotificationModal, setShowEmailNotificationModal] =
    useState(false);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);

  // Sync status tracking (for roadmap - Azure)
  const [roadmapSyncStatus, setRoadmapSyncStatus] = useState<string>("idle");
  const [roadmapLastSyncedAt, setRoadmapLastSyncedAt] = useState<string>("");
  const [showCompletionToast, setShowCompletionToast] = useState(false);
  const [completionMessage, setCompletionMessage] = useState("");

  // Project sync status tracking (for project - Jira)
  const [projectSyncStatus, setProjectSyncStatus] = useState<string>("idle");
  const [projectLastSyncedAt, setProjectLastSyncedAt] = useState<string>("");

  const [showProjectEmptyStateModal, setShowProjectEmptyStateModal] =
    useState(false);

  const org_id = jwtData.org_id;
  const { org_role = "" } = jwtData;

  // Ref to track if we've already processed a deep link task-id
  const processedTaskIdRef = useRef<string | null>(null);
  const {
    data: tasks,
    singleRoadmapLoading,
    skills_required,
    skill_members,
    singleRoadmapRes = [],
    singleRoadmapError,
    skill_capacity_percent,
    durations: projectDurations,
    duration: projectDuration,
    duration_with_delay,
    loading: isDataApiLoading,
    stats: projectStats,
    project_costs: projectCosts,
    is_sim_pending,
    status: projectStatus,
    start_date: projectStart,
    end_date: projectEnd,
    members: projectMembers,
    sim_data: projectSimulationData,
    members_data: projectMembersData,
    progress: projectProgress,
    child_entity_types: projectChildEntityTypes,
    entity_type: projectEntityType,
    projectApiPayload,
  } = useSelector((state: any) => state.roadmap);

  const {
    viewType,
    currentEntity,
    currentEntityId,
    dashboardDetails,
    taskIdForScroll,
    projectIdForScroll,
    simulation,
    roadmapName,
    projectName,
    roadmapPeriod,
    projectPeriod,
  } = useSelector((state: any) => state.timeline);

  const { hasSimulationTasks } = useSelector((state: any) => state.projects);

  const sim_flag = simulation;

  const hasTaskData = useMemo(() => {
    return Object.keys(taskData).length > 0;
  }, [taskData]);

  useEffect(() => {
    const actualTasks = tasks ? tasks.slice(1) : [];
    const hasActualTasks = actualTasks.length > 0;

    if (
      currentEntity === ENTITY.PROJECT &&
      !isDataApiLoading &&
      currentEntityId
    ) {
      if (!hasActualTasks) {
        setShowProjectEmptyStateModal(true);
      } else {
        setShowProjectEmptyStateModal(false);
      }
    }
  }, [currentEntity, tasks, isDataApiLoading, currentEntityId]);

  useEffect(() => {
    setShowProjectEmptyStateModal(false);
  }, [currentEntityId]);

  useEffect(() => {
    const hash = window.location.hash.substring(1); // Remove '#'
    if (!hash) return;

    const params = new URLSearchParams(hash);
    const entity = params.get("entity");
    const entityId = params.get("entity_id");

    if (entity && entityId && singleRoadmapRes.projects) {
      const project = singleRoadmapRes.projects.find(
        (el: any) => el.project_id === entityId,
      );
      dispatch(setRoadmapName(singleRoadmapRes.name));
      dispatch(setProjectName(project?.project_name));

      dispatch(
        setCurrentEntity({
          entity: entity as ENTITY,
          entityId: entityId,
        }),
      );
      dispatch(resetDashboardDetails());
      dispatch(resetRoadmap());
      dispatch(resetSelectedTaskId());
    }
  }, [singleRoadmapRes]);

  const getPeriod = () => {
    if (currentEntity === ENTITY.ROADMAP) {
      return roadmapPeriod;
    } else if (currentEntity === ENTITY.PROJECT) {
      return projectPeriod;
    }
  };

  const period = useMemo(() => {
    const current = getPeriod();
    if (!current) return undefined;
    return current === "daily" ? "weekly" : current;
  }, [currentEntity, roadmapPeriod, projectPeriod]);

  const handleRefreshClick = async (force_refresh = true) => {
    if (roadmap_id) {
      dispatch(resetDashboardDetails());
      if (currentEntity === ENTITY.PROJECT && currentEntityId) {
        fetchData(currentEntityId, simulation, {}, force_refresh);
      } else {
        fetchSingleRoadmap(roadmap_id, false, true);
        dispatch(resetProjectIdForScroll());
      }
      dispatch(resetTaskIdForScroll());
    } else if (
      effectiveProjectId &&
      currentEntity === ENTITY.PROJECT &&
      currentEntityId
    ) {
      dispatch(resetDashboardDetails());
      fetchData(currentEntityId, simulation, {}, force_refresh).then(() => {
        if (hasTaskData)
          window.dispatchEvent(new CustomEvent("capacity-planning:refresh"));
      });
      dispatch(resetTaskIdForScroll());
    }
  };

  // Keep a ref to handleRefreshClick for event listeners to avoid stale closures
  const handleRefreshClickRef = useRef(handleRefreshClick);
  useEffect(() => {
    handleRefreshClickRef.current = handleRefreshClick;
  }, [handleRefreshClick]);

  React.useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent)?.detail;
      const simWorkitemId = detail?.workitem_id;

      // Only auto-refresh if the simulated workitem matches the currently viewed entity
      if (simWorkitemId && currentEntityId && simWorkitemId !== currentEntityId) {
        return; // Different project — skip background refresh
      }

      // Enable simulation mode (moved here from useSSEChat so it only fires
      // when the simulated project matches the viewed project)
      dispatch(setSimulation(true));
      dispatch(resetRoadmap());
      handleRefreshClickRef.current(true);
    };

    window.addEventListener(
      "cadencepro:project-refresh",
      handler as EventListener,
    );
    return () =>
      window.removeEventListener(
        "cadencepro:project-refresh",
        handler as EventListener,
      );
  }, [currentEntityId]);

  const handleProjectClick = (
    project_id: string,
    isListView: boolean = false,
  ) => {
    const project = singleRoadmapRes.projects.find(
      (el: any) => el.project_id === project_id,
    );
    dispatch(setRoadmapName(singleRoadmapRes.name));
    dispatch(setProjectName(project?.project_name));
    dispatch(
    setCurrentEntity({
        entity: ENTITY.PROJECT,
        entityId: project_id,
      }),
    );
    window.location.hash = `entity=${ENTITY.PROJECT}&entity_id=${project_id}`;
    if (isListView) {
      dispatch(setViewType("list"));
    } else {
      dispatch(setViewType("timeline"));
    }
    // dispatch(setActiveTab("tasks"));
    dispatch(setProjectIdForScroll(`project-${project_id}`));
    dispatch(resetDashboardDetails());
    dispatch(resetRoadmap());
    dispatch(resetSelectedTaskId());
  };

  const handleEntityClick = (id: string) => {
    if (currentEntity === ENTITY.ROADMAP) {
      dispatch(resetProjectIdForScroll());
      setTimeout(() => {
        dispatch(setProjectIdForScroll(id));
      }, 20);
    } else if (currentEntity === ENTITY.PROJECT) {
      dispatch(resetTaskIdForScroll());
      setTimeout(() => {
        dispatch(setTaskIdForScroll(id));
      }, 20);
    }
  };

  const handleRoadmapClick = () => {
    if (!roadmap_id) return;
    const clone = structuredClone(projectIdForScroll);

    
 
    dispatch(setSingleRoadmap({}));

    dispatch(
      setCurrentEntity({
        entity: ENTITY.ROADMAP,
        entityId: roadmap_id,
      }),
    );

    setRoadmapInfoInRedux();
  
  };

  const handleSimulationToggle = () => {
    dispatch(setSimulation(!simulation));
    if (currentEntity === ENTITY.PROJECT) {
      dispatch(resetTaskIdForScroll());
    }
  };

  // Project-specific handlers
  const handleCreateTask = () => {
    if (template_id) {
      setShowCreateTaskModal(true);
      return;
    }

    if (!sim_flag) {
      setShowCreateTaskWarning(true);
    } else {
      setShowCreateTaskModal(true);
    }
  };

  const handleCreateTaskConfirm = () => {
    setShowCreateTaskWarning(false);
    setShowCreateTaskModal(true);
  };

  const handleEditProject = () => {
    if (currentEntityId) {
      setShowEditProjectModal(true);
    }
  };

  const handleEditTemplate = async () => {
    if (template_id) {
      try {
        const templateData: any = await getTemplateById(template_id);
        const description = templateData?.project_description;

        setTemplatePayloadData({
          template_id: template_id,
          project_id: currentProject?.project_id || template_id,
          project_name:
            currentProject?.project_name ||
            projectName ||
            templateData?.project_name ||
            "",
          project_description: description,
          include_delay:
            currentProject?.include_delay ||
            projectApiPayload?.include_delay ||
            templateData?.include_delay ||
            false,
          created_at:
            currentProject?.created_at ||
            projectApiPayload?.created_at ||
            templateData?.created_at,
        });
        setShowEditTemplateModal(true);
      } catch (error) {
        console.error("Error fetching template data:", error);
        if (currentProject) {
          setTemplatePayloadData({
            template_id: template_id,
            project_id: currentProject.project_id || template_id,
            project_name: currentProject.project_name || projectName || "",
            project_description:
              currentProject.project_description ||
              projectApiPayload?.project_description ||
              "",
            include_delay: currentProject.include_delay || false,
            created_at: currentProject.created_at,
          });
          setShowEditTemplateModal(true);
        }
      }
    }
  };

  const handleDeleteProject = async (project: any) => {
    try {
      await deleteProjects(project.project_id, jwtData.org_id);
      // Refresh the roadmap data after deletion
      if (currentEntityId) {
        fetchSingleRoadmap(currentEntityId);
      }
    } catch (error) {
      console.error("Error deleting project:", error);
    }
  };

  const handleMerge = () => {
    setShowMergeWarningModal(true);
  };

  const handleReset = () => {
    setShowResetWarningModal(true);
  };

  const handleVersionHistory = () => {
    if (currentEntityId) {
      fetchProjectVersion(currentEntityId);
      setShowVersionHistoryModal(true);
    }
  };

  const handleProjectHistory = () => {
    setShowProjectHistoryModal(true);
  };

  const handleTaskModalClose = () => {
    setShowCreateTaskModal(false);
    setSelectedTaskForEdit(null);
    processedTaskIdRef.current = null; // Reset so deep link can work again

    // Remove task-id query parameter from URL
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has("task-id")) {
      searchParams.delete("task-id");
      const newSearch = searchParams.toString();
      const newUrl = newSearch
        ? `${window.location.pathname}?${newSearch}`
        : window.location.pathname;
      navigate(newUrl, { replace: true });
    }
  };

  const handleTaskUpdate = () => {
    // Refresh data after task update
    if (currentEntity === ENTITY.PROJECT && currentEntityId) {
      dispatch(resetDashboardDetails());
      fetchData(currentEntityId, simulation, {}, true).then(() => {
        // if (roadmap_id) fetchSingleRoadmap(roadmap_id, false);
        if (hasTaskData)
          window.dispatchEvent(new CustomEvent("capacity-planning:refresh"));
      });
    }
    handleTaskModalClose();
  };

  const handleShowModal = (flag: boolean) => {
    setShowRoadMapModal(flag);
    if (!flag) setShouldScrollToTabs(false);
  };

  const handleLinkItems = () => {
    setShowRoadMapModal(true);
    setShouldScrollToTabs(true);
  };

  // Format entity type: "roadmap" -> "Strategy Map", "project" -> "Work Unit", others capitalize first letter
  const formatEntityType = (entityType: string): string => {
    const lowerType = entityType.toLowerCase();
    if (lowerType === "roadmap") return "Strategy Map";
    if (lowerType === "project") return "Work Unit";
    return (
      entityType.charAt(0).toUpperCase() + entityType.slice(1).toLowerCase()
    );
  };

  // Define breadcrumbs - make them dynamic based on current entity
  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const baseBreadcrumbs: BreadcrumbItem[] = [];

    // For roadmap pages: Use entity type from singleRoadmapRes (roadmap API) - this won't change when project API is called
    // For standalone project pages: Use projectEntityType (project API)

    const roadmapEntityTypeFromApi = singleRoadmapRes?.entity_type
      ? formatEntityType(singleRoadmapRes.entity_type)
      : null;

    const isRoadmapLoading = singleRoadmapLoading && !roadmapEntityTypeFromApi;
    const isProjectLoading = isDataApiLoading && !dashboardDetails?.entityType;

    // Roadmap entity label - uses roadmap API's entity_type directly (singular form)
    const roadmapEntityLabel = isRoadmapLoading
      ? "Loading..."
      : roadmapEntityTypeFromApi || "Strategy Map";

    // Project entity label - uses project API's entity_type (for standalone projects only)
    const projectEntityTypeFormatted = projectEntityType
      ? formatEntityType(projectEntityType)
      : null;
    const projectEntityLabel =
      isProjectLoading && !projectEntityTypeFormatted
        ? "Loading..."
        : projectEntityTypeFormatted || "Work Unit";

    if (roadmap_id) {
      // Check if accessed from Projects page (/roadmaps route) or Strategy Maps page (/strategymaps route)
      const isFromProjects = location.pathname.includes("/roadmaps/");
      const backHref = isFromProjects ? "/workitems" : "/strategymaps";

      // baseBreadcrumbs.push({ label: roadmapEntityLabel, href: backHref })
      if (currentEntity === ENTITY.ROADMAP) {
        baseBreadcrumbs.push({
          label: roadmapName || "Loading...",
          href: "#",
          active: true,
        });
      } else if (currentEntity === ENTITY.PROJECT) {
        // Add roadmap as clickable breadcrumb
        baseBreadcrumbs.push({
          label: roadmapName || "Loading...",
          href: "#",
          active: false,
        });

        baseBreadcrumbs.push({
          label: projectName || "Loading...",
          href: "#",
          active: true,
        });
      }
    } else if (effectiveProjectId && currentEntity === ENTITY.PROJECT) {
      if (template_id) {
        baseBreadcrumbs.push({ label: "Templates", href: "/templates" });
      } else {
        // baseBreadcrumbs.push({ label: projectEntityLabel, href: '/workitems' })
      }
      baseBreadcrumbs.push({
        label: projectName || "Loading...",
        href: "#",
        active: true,
      });
    }

    return baseBreadcrumbs;
  };

  const breadcrumbs = React.useMemo(() => {
    const isEntityTypeLoading = singleRoadmapLoading || isDataApiLoading;
    if (readOnly && currentEntity === ENTITY.PROJECT) {
      const breadcrumbs: BreadcrumbItem[] = [];
      const entityTypeFormatted = projectEntityType
        ? formatEntityType(projectEntityType)
        : null;
      const entityLabel =
        isEntityTypeLoading && !entityTypeFormatted
          ? "Loading..."
          : entityTypeFormatted || "Work Unit";
      if (template_id) {
        breadcrumbs.push({ label: "Templates", href: "/templates" });
      } else {
        breadcrumbs.push({ label: entityLabel, href: "/workitems" });
      }
      breadcrumbs.push({
        label: projectName || "Loading...",
        href: "#",
        active: true,
      });
      return breadcrumbs;
    }
    return getBreadcrumbs();
  }, [
    readOnly,
    currentEntity,
    roadmap_id,
    template_id,
    projectName,
    roadmapName,
    effectiveProjectId,
    singleRoadmapLoading,
    isDataApiLoading,
    singleRoadmapRes?.entity_type,
    projectEntityType,
  ]);

  const setRoadmapInfoInRedux = () => {
    dispatch(setRoadmapName(singleRoadmapRes.name));
    dispatch(
      setDashboardDetails({
        status: singleRoadmapRes.status,
        date:
          moment(singleRoadmapRes.start_date).format("DD/MM/YYYY") +
          " - " +
          moment(singleRoadmapRes.end_date).format("DD/MM/YYYY"),
        progress: singleRoadmapRes.progress,
        stats: singleRoadmapRes.stats,
        sim_data: singleRoadmapRes.sim_data,
        skills: [],
        entityType:
          ChildEntityTypeConfig[
            singleRoadmapRes.entity_type as keyof typeof ChildEntityTypeConfig
          ] || "Entities",
        childEntityType: "Work Items",
        childEntityTypes: singleRoadmapRes.child_entity_types,
        members: singleRoadmapRes.members,
      }),
    );
  };

  useEffect(() => {
    if (org_id) {
      if (!hasTaskData) {
        fetchManagerResources(org_id);
      }
      fetchOrgSchedule(org_id);
    }
  }, [org_id]);

  useEffect(() => {
    if (roadmap_id && Object.keys(singleRoadmapRes).length === 0) {
      fetchSingleRoadmap(roadmap_id, false, false, true);
    }
  }, [singleRoadmapRes]);

  useEffect(() => {
    if (readOnly) return;
    if (currentEntity === ENTITY.PROJECT && currentEntityId) {
      dispatch(resetDashboardDetails());
      fetchData(currentEntityId, simulation, {});
    }
  }, [simulation, currentEntityId, readOnly]);

  // Handle deep linking - open task modal when task-id is in URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const taskIdFromUrl = searchParams.get("task-id");

    // Skip if no task-id in URL, or already processed this task-id, or already showing modal
    if (
      !taskIdFromUrl ||
      processedTaskIdRef.current === taskIdFromUrl ||
      showCreateTaskModal
    ) {
      return;
    }

    // Wait for tasks data to be available and we're in project context
    if (
      currentEntity === ENTITY.PROJECT &&
      tasks?.length > 1 &&
      !isDataApiLoading
    ) {
      // Find the task in the tasks list (skip first element which is the project itself)
      const actualTasks = tasks.slice(1);
      const taskToOpen = actualTasks.find(
        (t: any) => t.item_id === taskIdFromUrl || t.id === taskIdFromUrl,
      );

      if (taskToOpen) {
        processedTaskIdRef.current = taskIdFromUrl;
        setSelectedTaskForEdit(taskToOpen);
        setShowCreateTaskModal(true);
      }
    }
  }, [
    location.search,
    tasks,
    isDataApiLoading,
    showCreateTaskModal,
    currentEntity,
  ]);

  useEffect(() => {
    if (template_id) {
      dispatch(setTemplateId(template_id));
    } else {
      dispatch(resetTemplateId());
    }
  }, [template_id, dispatch]);

  useEffect(() => {
    if (
      roadmap_id &&
      (currentEntity === null || currentEntity === ENTITY.ROADMAP)
    ) {
      dispatch(resetDashboardDetails());
      dispatch(
        setCurrentEntity({
          entity: ENTITY.ROADMAP,
          entityId: roadmap_id,
        }),
      );
    } else if (effectiveProjectId) {
      dispatch(resetDashboardDetails());
      dispatch(
        setCurrentEntity({
          entity: ENTITY.PROJECT,
          entityId: effectiveProjectId,
        }),
      );
    }
  }, [roadmap_id, effectiveProjectId]);

  useEffect(() => {
    if (currentEntity === ENTITY.ROADMAP) {
      setRoadmapInfoInRedux();
      const clone = structuredClone(projectIdForScroll);
      dispatch(resetProjectIdForScroll());
      setTimeout(() => {
        dispatch(setProjectIdForScroll(clone));
      }, 20);
    }
  }, [singleRoadmapRes]);

  // useEffect(() => {
  //   if(viewType === 'timeline') {
  //     let clone: any = null;
  //     if(currentEntity === ENTITY.ROADMAP) {
  //       clone = structuredClone(projectIdForScroll)
  //       dispatch(resetProjectIdForScroll())
  //     } else if (currentEntity === ENTITY.PROJECT) {
  //       clone = structuredClone(taskIdForScroll)
  //       dispatch(resetTaskIdForScroll())
  //     }
  //
  //     setTimeout(() => {
  //       if(currentEntity === ENTITY.ROADMAP) {
  //         dispatch(setProjectIdForScroll(clone))
  //       } else if (currentEntity === ENTITY.PROJECT) {
  //         dispatch(setTaskIdForScroll(clone))
  //       }
  //     }, 500)
  //   }
  // }, [viewType]);

  // @ts-ignore
  useEffect(() => {
    if (isDataApiLoading) {
      dispatch(
        setDashboardDetails({
          stats: {},
          date: "",
          skills: [], // Show dummy skills during loading
          progress: 0,
          status: "",
          members: [],
          entityType: dashboardDetails.entityType,
          childEntityType: dashboardDetails.childEntityType,
        }),
      );
    } else if (
      Object.keys(projectStats).length !== 0 &&
      currentEntity === ENTITY.PROJECT
    ) {
      if (singleRoadmapRes?.projects && singleRoadmapRes.projects.length > 0) {
        const project = singleRoadmapRes?.projects?.find(
          (project: any) => project.project_id === currentEntityId,
        );
        if (project) {
          dispatch(setProjectName(project?.project_name));
        } else {
          dispatch(setProjectName(tasks[0]?.name));
        }
      } else if (tasks.length >= 1) {
        dispatch(setProjectName(tasks[0]?.name));
      }

      dispatch(
        setDashboardDetails({
          stats: projectStats,
          progress: projectProgress,
          skills: skills_required,
          entityType:
            ChildEntityTypeConfig[
              projectEntityType as keyof typeof ChildEntityTypeConfig
            ] || "Entities",
          childEntityType: "Work Items",
          childEntityTypes: projectChildEntityTypes,
          status: projectStatus,
          date:
            moment(projectStart).format("DD/MM/YYYY") +
            " - " +
            moment(projectEnd).format("DD/MM/YYYY"),
          members: projectMembers,
          sim_data: projectSimulationData,
        }),
      );
    }
  }, [
    isDataApiLoading,
    simulation,
    projectStats,
    projectProgress,
    projectStatus,
    projectStart,
    projectEnd,
    projectMembers,
    skills_required,
    projectEntityType,
    projectChildEntityTypes,
    currentEntityId,
    singleRoadmapRes,
  ]);

  // Get current project data
  const getCurrentProject = () => {
    if (currentEntity === ENTITY.PROJECT && currentEntityId) {
      if (projectApiPayload) {
        
        // Added patch, due to inconsistency in key name of id in apis
        return {project_id: projectApiPayload?.id, ...projectApiPayload};
      } else if (tasks.length >= 1) {
        const project = tasks[0];

        return {
          project_id: project.id,
          integration_data: project.integration_data,
        };
      }
    }

    return null;
  };

  const currentProject = getCurrentProject();

  // Function to get duration for display
  const getDurationDisplay = () => {
    if (isDataApiLoading) {
      return "0"; // Show "0" during loading instead of old values
    }
    if (currentEntity === ENTITY.PROJECT && duration_with_delay) {
      return duration_with_delay;
    }
    return "";
  };

  const handleShare = () => {
    if (currentEntityId) {
      setShowShareModal(true);
      fetchProjectShareTokens(currentEntityId);
    }
  };

  const getCost = () => {
    if (currentEntity === ENTITY.ROADMAP) {
      const costs = simulation
        ? singleRoadmapRes?.sim_data?.project_costs
        : singleRoadmapRes?.project_costs;

      return costs?.total_cost ?? 0;
    }

    if (currentEntity === ENTITY.PROJECT) {
      return projectCosts?.total_cost ?? 0;
    }

    return 0;
  };

  const handleSyncTickets = () => {
    if (currentEntityId) {
      setShowSyncModal(true);
      fetchProjectIntegrations(currentEntityId);
    }
  };

  const handleResyncRoadmap = async () => {
    const integration = singleRoadmapRes?.integration_data;

    // Only allow resync for Azure roadmaps
    if (
      currentEntity === ENTITY.ROADMAP &&
      currentEntityId &&
      integration?.integration_type === "azure"
    ) {
      await resyncEpicWithStatus(currentEntityId, integration.integration_type);
      // Note: We don't call fetchSingleRoadmap here anymore since it will be called from the refresh button
    }
  };

  // Handle resync for Jira projects
  const handleResyncProject = async () => {
    const integration = currentProject?.integration_data;

    // Only allow resync for Jira projects
    if (
      currentEntity === ENTITY.PROJECT &&
      currentEntityId &&
      integration?.integration_type === "jira"
    ) {
      // Update project sync status tracking
      setProjectSyncStatus("in_progress");

      try {
        await resyncEpicWithStatus(
          currentEntityId,
          integration.integration_type,
        );
      } catch (error) {
        console.error("Project resync failed:", error);
        setProjectSyncStatus("failed");
      }
    }
  };

  // Fetch current sync status from backend (only for Azure integrations - roadmap level)
  useEffect(() => {
    const integration = singleRoadmapRes?.integration_data;

    if (roadmap_id && integration?.integration_type === "azure") {
      const fetchCurrentSyncStatus = async () => {
        try {
          const currentStatus = await getResyncStatus(
            integration.integration_type,
            roadmap_id,
          );
          if (currentStatus) {
            setRoadmapLastSyncedAt(currentStatus.last_synced_at || "");
            setRoadmapSyncStatus(currentStatus.sync_status || "idle");
          }
        } catch (error) {
          console.log("Could not fetch sync status:", error);
          // Reset to defaults if API fails
          setRoadmapLastSyncedAt("");
          setRoadmapSyncStatus("idle");
        }
      };

      fetchCurrentSyncStatus();
    }
  }, [roadmap_id, singleRoadmapRes?.integration_data?.integration_type]);

  // Fetch current sync status for Jira projects (project level)
  useEffect(() => {
    const integration = currentProject?.integration_data;

    if (
      currentEntity === ENTITY.PROJECT &&
      currentEntityId &&
      integration?.integration_type === "jira"
    ) {
      const fetchCurrentProjectSyncStatus = async () => {
        try {
          const currentStatus = await getResyncStatus(
            integration.integration_type,
            currentEntityId,
          );
          if (currentStatus) {
            setProjectLastSyncedAt(currentStatus.last_synced_at || "");
            setProjectSyncStatus(currentStatus.sync_status || "idle");
          }
        } catch (error) {
          console.log("Could not fetch project sync status:", error);
          // Reset to defaults if API fails
          setProjectLastSyncedAt("");
          setProjectSyncStatus("idle");
        }
      };

      fetchCurrentProjectSyncStatus();
    }
  }, [
    currentEntity,
    currentEntityId,
    currentProject?.integration_data?.integration_type,
  ]);

  // Set up sync callbacks - handles both roadmap and project syncs
  useEffect(() => {
    setIntegrationSyncStatusCallback((status: string, data?: any) => {
      // Determine if this is roadmap or project sync based on current entity
      if (currentEntity === ENTITY.ROADMAP) {
        setRoadmapSyncStatus(status);
        if (status === "completed" && data?.last_synced_at) {
          setRoadmapLastSyncedAt(data.last_synced_at);
        }
      } else if (currentEntity === ENTITY.PROJECT) {
        setProjectSyncStatus(status);
        if (status === "completed" && data?.last_synced_at) {
          setProjectLastSyncedAt(data.last_synced_at);
        }
      }
    });

    setIntegrationSyncCompletionCallback(
      (message: string, lastSyncedAt: string) => {
        setCompletionMessage(message);
        if (currentEntity === ENTITY.ROADMAP) {
          setRoadmapLastSyncedAt(lastSyncedAt);
          setRoadmapSyncStatus("completed");
        } else if (currentEntity === ENTITY.PROJECT) {
          setProjectLastSyncedAt(lastSyncedAt);
          setProjectSyncStatus("completed");
        }
        setShowCompletionToast(true);
      },
    );
  }, [roadmap_id, currentEntity]);

  // Handle refresh from completion toast
  const handleRefreshFromToast = () => {
    if (currentEntity === ENTITY.ROADMAP && currentEntityId) {
      dispatch(resetDashboardDetails());
      fetchSingleRoadmap(currentEntityId, false, true);
    } else if (currentEntity === ENTITY.PROJECT && currentEntityId) {
      dispatch(resetDashboardDetails());
      fetchData(currentEntityId, simulation, {}, true);
    }
    setShowCompletionToast(false);
  };

  const openProjectDetailsModal = async (project: any) => {
    setIsModalOpen(true);
    setModalLoading(true);
    try {
      const projectData = await fetchProjectDashboard(
        project.project_id,
        simulation,
      );
      setSelectedProject(projectData);
      setModalLoading(false);
    } catch (error) {
      setModalLoading(false);
      setSelectedProject(null);
      setIsModalOpen(false);
      console.error("Error fetching project details:", error);
    }
  };

  const handleTaskClick = (task: any) => {
    setSelectedTaskForEdit(task);
    setShowCreateTaskModal(true);
  };

  const handleGTProjectClick = (project: any) => {
    handleProjectClick(project.project_id);
  };

  const { previousRoute } = useSelector((state: any) => state.navigationInfo);
  const handleBack = () => {
    window.location.hash = "";
    if (onBack) {
      onBack();
      return;
    }


    if (roadmap_id) {
      if (currentEntity === ENTITY.PROJECT) {
        handleRoadmapClick();
      } else if (currentEntity === ENTITY.ROADMAP) {
        // Check if accessed from Projects page (/roadmaps route) or Strategy Maps page (/strategymaps route)
        const isStragyMap = previousRoute?.includes("/strategymaps");
        if (previousRoute && isStragyMap) {
          navigate(previousRoute);
          return;
        }
        const isFromProjects = location.pathname.includes("/roadmaps/");
        navigate(isFromProjects ? "/workitems" : "/strategymaps");
      }
    } else if (effectiveProjectId) {
      const isStragyMap = previousRoute?.includes("/strategymaps");

      if (previousRoute && isStragyMap) {
        navigate(previousRoute);
        return;
      }
      if (template_id) {
        navigate("/templates");
      } else {
        navigate("/workitems");
      }
    }
  };

  const handleBreadCrumbClick = (index: number) => {

    window.location.hash = "";
    if (roadmap_id) {
      if (index === 0 && currentEntity === ENTITY.PROJECT) {
        handleRoadmapClick();
      }
    } else if (effectiveProjectId && currentEntity === ENTITY.PROJECT) {
      if (template_id) {
        if (index === 0) {
          navigate("/templates");
        }
      } else {
        if (index === 0) {
          navigate("/workitems");
        }
      }
    }
  };

  // useEffect(() => {
  //   const scrollToCurrentDateFlag = () => {
  //     const element = document.getElementById('current-date-flag');
  //     const timelineContainer = document.getElementById("timeline-container")
  //
  //     setTimeout(() => {
  //       if (element) {
  //         element.scrollIntoView({
  //           behavior: 'auto',
  //           block: 'start',
  //           inline: 'center'
  //         });
  //       } else if(timelineContainer) {
  //         timelineContainer.scrollLeft = 0;
  //         timelineContainer.scrollTop = 0;
  //       }
  //     }, 20)
  //   };
  //
  //   if(!projectIdForScroll || !taskIdForScroll) {
  //     scrollToCurrentDateFlag();
  //   }
  // }, [period, viewType]);

  useEffect(() => {
    if (readOnly) return;
    if (hasTaskData) {
      dispatch(
        setTaskIdForScroll(`task__${taskData.project_id}__${taskData.item_id}`),
      );
    }
  }, [taskData, readOnly]);

  useEffect(() => {
    if (
      !isDataApiLoading &&
      taskIdForScroll &&
      currentEntity === ENTITY.PROJECT &&
      currentEntityId &&
      window.location.pathname.includes("/resources")
    ) {
      setTimeout(() => {
        const locateTaskObj = {
          detail: {
            projectId: currentEntityId,
            taskId: taskIdForScroll.split("__")[2] || "",
            uniqueTaskId: taskIdForScroll,
          },
        };

        window.dispatchEvent(
          new CustomEvent("cadencepro:locate-task", locateTaskObj),
        );
      }, 200);
    }
  }, [isDataApiLoading, viewType]);

  useEffect(() => {
    if (singleRoadmapError) {
      const errorMessage =
        singleRoadmapError?.response?.data?.detail ||
        singleRoadmapError?.message ||
        singleRoadmapError?.detail ||
        "";
      if (
        errorMessage.toLowerCase().includes("not found") ||
        errorMessage.toLowerCase().includes("entity: roadmap with id:")
      ) {
        navigate("/strategymaps");
      }
    }
  }, [singleRoadmapError, navigate]);

  const getWidth = () => {
    if (readOnly) return;

    if (hasTaskData) return "calc(100vw - 20vw)";

    if (isOpenOnSmallScreens) {
      return "calc(100vw - 65px)";
    } else {
      return "calc(100vw - 255px)";
    }
  };

  const handleUpdateProjectStatusToClosed = async () => {
    if (currentEntity === ENTITY.ROADMAP && currentEntityId) {
      const payload = {
        dependencies: singleRoadmapRes?.dependencies || [],
        description: singleRoadmapRes?.description || "",
        entity_type: "epic",
        milestones: singleRoadmapRes?.milestones || [],
        name: singleRoadmapRes?.name || "",
        priority: singleRoadmapRes?.priority || "",
        projects:
          singleRoadmapRes?.projects?.map((p: any) => p.project_id) || [],
        stage: "closed",
      };
      await toast.promise(updateRoadmap(currentEntityId, payload), {
        loading: "Updating status...",
        success: "Status updated successfully",
        error: "Failed to update status",
      });
      handleRefreshClick();
    } else if (currentEntityId && projectApiPayload) {
      const payload = {
        ...projectApiPayload,
        stage: "closed",
        org_id,
      };

      if (payload.start_date) {
        payload.start_date = moment(payload.start_date).format("YYYY-MM-DD");
      }

      await updateProject(currentEntityId, payload);
      handleRefreshClick();
    }
  };

  return (
    <div
      className="row bg-tertiary-light dark:bg-tertiary-dark w-full relative flex flex-col h-[100vh] flex-1"
      style={{ width: getWidth() }}
    >
      {/* Add the BreadcrumbHeader component */}
      <BreadcrumbHeader
        breadcrumbs={breadcrumbs}
        showSimToggle={true}
        simFlag={sim_flag}
        onToggleSim={handleSimulationToggle}
        showRefreshButton={!readOnly}
        onRefresh={!readOnly ? handleRefreshClick : undefined}
        setShowRoadmapModal={setShowRoadMapModal}
        isLoading={
          currentEntity === ENTITY.PROJECT
            ? isDataApiLoading
            : singleRoadmapLoading
        }
        onBreadcrumbClick={(index: number, item: BreadcrumbItem) =>
          handleBreadCrumbClick(index)
        }
        onCreateTask={!readOnly ? handleCreateTask : undefined}
        onEditProject={!readOnly ? handleEditProject : undefined}
        onMerge={!readOnly ? handleMerge : undefined}
        onReset={!readOnly ? handleReset : undefined}
        onVersionHistory={!readOnly ? handleVersionHistory : undefined}
        onProjectHistory={!readOnly ? handleProjectHistory : undefined}
        onShare={!readOnly ? handleShare : undefined}
        onSyncTickets={!readOnly ? handleSyncTickets : undefined}
        onNotifications={
          !readOnly ? () => setShowEmailNotificationModal(true) : undefined
        }
        selectedProject={currentProject}
        sim_flag={sim_flag}
        org_role={org_role}
        simulationChanges={{
          hasChanges: Boolean(is_sim_pending),
        }}
        onResyncRoadmap={!readOnly ? handleResyncRoadmap : undefined}
        onResyncProject={!readOnly ? handleResyncProject : undefined}
        integration_data={
          currentEntity === ENTITY.ROADMAP
            ? singleRoadmapRes?.integration_data
            : null
        }
        projectIntegrationData={
          currentEntity === ENTITY.PROJECT
            ? currentProject?.integration_data
            : null
        }
        syncStatus={roadmapSyncStatus}
        lastSyncedAt={roadmapLastSyncedAt}
        projectSyncStatus={projectSyncStatus}
        projectLastSyncedAt={projectLastSyncedAt}
        readOnly={readOnly || !!template_id}
        onEditTemplate={template_id ? handleEditTemplate : undefined}
        templateData={template_id ? currentProject : undefined}
        onLinkItems={handleLinkItems}
      />

      {/* Add the TopNavBar component */}
      <TopNavBar
        title={currentEntity === ENTITY.ROADMAP ? roadmapName : projectName}
        childEntityType={dashboardDetails.childEntityType}
        childEntityTypes={dashboardDetails.childEntityTypes}
        status={dashboardDetails.status}
        progress={dashboardDetails.progress}
        simFlag={sim_flag}
        sim_data={dashboardDetails?.sim_data}
        stats={dashboardDetails.stats}
        cost={getCost()}
        totalTasks={dashboardDetails.stats?.total || 0}
        members={dashboardDetails.members}
        skills={dashboardDetails.skills}
        duration_with_delay={getDurationDisplay()}
        isLoading={
          currentEntity === ENTITY.PROJECT
            ? isDataApiLoading
            : singleRoadmapLoading
        }
        entityType={currentEntity}
        integration_data={
          currentEntity === ENTITY.ROADMAP
            ? singleRoadmapRes?.integration_data
            : currentProject?.integration_data
        }
        setShowSkillsModal={setShowSkillsModal}
        handleGoBack={handleBack}
        readOnly={readOnly}
      />

      <TimelineNavBar
        period={period}
        setPeriod={(period: ViewType) => {
          if (currentEntity === ENTITY.ROADMAP) {
            dispatch(setRoadmapPeriod(period));
          } else {
            dispatch(setProjectPeriod(period));
          }
        }}
        stats={dashboardDetails.stats}
        date={dashboardDetails.date}
        isLoading={
          currentEntity === ENTITY.PROJECT
            ? isDataApiLoading
            : singleRoadmapLoading
        }
        simFlag={sim_flag}
        status={dashboardDetails.status}
        sim_data={dashboardDetails?.sim_data}
      />

      {dashboardDetails.status == DashboardStatus.COMPLETED && (
        <Alert
          variant="info"
          message={
            <>
              All work items of this Work Item is completed, Do you want to
              change status to <b>Closed</b>?
            </>
          }
          action={{
            label: "Yes, Update to Closed",
            onClick: handleUpdateProjectStatusToClosed,
          }}
        ></Alert>
      )}
      {/* Custom completion toast */}
      {showCompletionToast && (
        <SyncCompletionToast
          message={completionMessage}
          onRefresh={handleRefreshFromToast}
          onClose={() => setShowCompletionToast(false)}
          lastSyncedAt={
            currentEntity === ENTITY.PROJECT
              ? projectLastSyncedAt
              : roadmapLastSyncedAt
          }
        />
      )}

      <div
        className={`${viewType === "timeline" ? "!invisible !hidden" : "visible !flex"} flex flex-col h-full overflow-hidden`}
      >
        {currentEntity === ENTITY.ROADMAP && currentEntityId && (
          <div className="flex-1 min-h-0 px-4">
            <ListViewTable
              data={singleRoadmapRes.projects}
              handleRefreshClick={()=>handleRefreshClick(false)}
              handleRowClick={openProjectDetailsModal}
              handleProjectClick={handleProjectClick}
              onDelete={handleDeleteProject}
              loading={singleRoadmapLoading}
              openEditProjectModal={(project: any) => {
                if (project?.project_id) {
                  dispatch(
                    setCurrentEntity({
                      entity: ENTITY.PROJECT,
                      entityId: project.project_id,
                    }),
                  );
                }
                setShowEditProjectModal(true);
              }}
            />
          </div>
        )}

        {currentEntity === ENTITY.PROJECT && currentEntityId && (
          <ProjectListView
            tasks={tasks}
            projectData={currentProject}
            isDataApiLoading={isDataApiLoading}
            sim_flag={sim_flag}
            hasSimulationTasks={hasSimulationTasks}
            readOnly={readOnly}
            handleTaskClick={handleTaskClick}
            onToggleSim={handleSimulationToggle}
            onMerge={!readOnly ? handleMerge : undefined}
            onTaskUpdate={handleTaskUpdate}
          />
        )}
      </div>

      <section
        className={`${viewType === "list" ? "!invisible !h-0" : "!visible !h-full"} bg-light-surface dark:bg-tertiary-dark h-full text-light-text-primary dark:text-white flex flex-row relative overflow-hidden`}
      >
        <PanelGroup direction="horizontal">
          <Panel
            ref={sidebarPanelRef}
            collapsible={false}
            minSize={sidebarCollapsed ? 3 : 20}
            defaultSize={20}
            maxSize={40}
          >
            <RoadmapSidebar
              childEntityType={dashboardDetails.childEntityType}
              projects={singleRoadmapRes.projects || []}
              currentRoadmapId={String(roadmap_id)}
              collapsed={sidebarCollapsed}
              readOnly={readOnly}
              setCollapsed={(collapsed) => {
                setSidebarCollapsed(collapsed);
                if (sidebarPanelRef.current) {
                  setTimeout(() => {
                    if (sidebarPanelRef.current) {
                      if (collapsed) {
                        sidebarPanelRef.current.resize(3);
                      } else {
                        sidebarPanelRef.current.resize(20);
                      }
                    }
                  }, 10);
                }
              }}
              onEntityClick={handleEntityClick}
              onTimelineClick={handleProjectClick}
            />
          </Panel>

          <PanelResizeHandle
            disabled={sidebarCollapsed}
            className={`w-0 relative ${
              sidebarCollapsed ? "cursor-default" : "cursor-col-resize"
            }`}
          >
            <div
              className={`absolute top-1/2 -translate-y-1/2 -left-2 bg-light-surfacePrimary dark:bg-secondary-dark flex flex-row border border-gray-200 dark:border-gray-700 rounded-full p-1 gap-0.5 transition-opacity ${sidebarCollapsed ? "opacity-0" : "opacity-100"}`}
            >
              <div className="w-0.5 h-4 bg-gray-400 dark:bg-gray-500 rounded-full"></div>
              <div className="w-0.5 h-4 bg-gray-400 dark:bg-gray-500 rounded-full"></div>
            </div>
          </PanelResizeHandle>

          <Panel>
            <div className="flex-1 overflow-hidden h-full relative">
              {currentEntity === ENTITY.ROADMAP && singleRoadmapLoading && (
                <LoadingState />
              )}

              {currentEntity === ENTITY.PROJECT && isDataApiLoading && (
                <LoadingState />
              )}

              {!isDataApiLoading &&
                currentEntity === ENTITY.PROJECT &&
                (!tasks || tasks.length <= 1) && (
                  <ProjectEmptyStateModal
                    showModal={showProjectEmptyStateModal}
                    entityType={dashboardDetails.childEntityType}
                    simFlag={sim_flag}
                    hasSimulationTasks={hasSimulationTasks}
                    onToggleSim={handleSimulationToggle}
                    onMerge={handleMerge}
                  />
                  // <EmptyState simFlag={sim_flag} hasSimulationTasks={hasSimulationTasks} entityType={dashboardDetails.childEntityType} />
                )}

              {currentEntity === ENTITY.ROADMAP &&
                (!singleRoadmapRes ||
                  singleRoadmapRes?.projects?.length === 0) && (
                  <EmptyState
                    simFlag={sim_flag}
                    hasSimulationTasks={hasSimulationTasks}
                    entityType={dashboardDetails.childEntityType}
                  />
                )}

              <div
                className="w-full h-full overflow-auto cursor-grab select-none overscroll-contain"
                id="timeline-container"
                ref={ref}
                {...events}
                onDragStart={(e) => e.preventDefault()}
                // style={{ overscrollBehavior: 'contain' }}
              >
                {(currentEntity === ENTITY.ROADMAP ||
                  (currentEntity === ENTITY.PROJECT &&
                    !showProjectEmptyStateModal &&
                    tasks &&
                    tasks.slice(1).length > 0)) && (
                  <GanttViewTimeline
                    onProjectClick={handleGTProjectClick}
                    onTaskClick={
                      readOnly ? ((() => {}) as any) : handleTaskClick
                    }
                    simFlag={sim_flag}
                    orgId={org_id}
                    viewType={period}
                  />
                )}
              </div>

              {(currentEntity === ENTITY.ROADMAP
                ? singleRoadmapRes?.projects?.length > 0
                : tasks?.length > 1) &&
                viewType === "timeline" && (
                  <BirdsEyeView
                    viewType={period}
                    containerId="timeline-container"
                  />
                )}
            </div>
          </Panel>
        </PanelGroup>
      </section>

      {/* Modals */}
      {showRoadMapModal ? (
        <CreateAdvancedEpicModal
          isVisible={showRoadMapModal}
          setShowModal={handleShowModal}
          roadmap={{
            ...singleRoadmapRes,
          }}
          shouldScrollToTabs={shouldScrollToTabs}
          setSelectedRoadmap={async () => {
            if (currentEntity === ENTITY.ROADMAP && currentEntityId) {
              dispatch(resetDashboardDetails());
              await fetchSingleRoadmap(currentEntityId, false);
            }
          }}
        />
      ) : (
        ""
      )}

      {/* Add ProjectDetailsModal */}
      {isModalOpen && (
        <ProjectDetailsModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedProject(null);
            setModalLoading(false);
          }}
          project={selectedProject ?? {}}
          loading={Boolean(modalLoading) || !selectedProject}
        />
      )}

      {/* Create/Edit Task Modal */}
      {showCreateTaskModal &&
        currentEntity === ENTITY.PROJECT &&
        currentEntityId &&
        tasks && (
          <TaskCompletionUpdateModal
            project_id={currentEntityId}
            isVisiable={showCreateTaskModal}
            handleAddNewTask={(flag: boolean) => {
              if (!flag) {
                handleTaskModalClose();
              }
            }}
            onClose={handleTaskModalClose}
            onTaskUpdate={handleTaskUpdate}
            tasks={tasks ?? []}
            selectedTask={selectedTaskForEdit ?? {}}
            members={projectMembers}
            sim_flag={sim_flag}
          />
        )}

      {/* Edit Project Modal */}
      {showEditProjectModal && currentProject && (
        <AddUpdateProjectModal
          isVisible={showEditProjectModal}
          setShowModal={setShowEditProjectModal}
          project={currentProject}
          onProjectUpdate={handleTaskUpdate}
        />
      )}

      {showEditTemplateModal && template_id && (
        <AddUpdateTemplate
          toggleCreateModal={showEditTemplateModal}
          setToggleCreateModal={setShowEditTemplateModal}
          payloadData={templatePayloadData}
          setPayloadData={setTemplatePayloadData}
          onSuccess={async () => {
            if (template_id) {
              const updatedTemplateData: any =
                await getTemplateById(template_id);

              if (updatedTemplateData?.project_name) {
                dispatch(setProjectName(updatedTemplateData.project_name));
              }

              if (currentEntityId) {
                fetchData(currentEntityId, simulation, {}, true);
              }
            }
          }}
        />
      )}

      {/* Skills Modal */}
      {showSkillsModal && currentEntity === ENTITY.PROJECT && (
        <SkillsModal
          showModal={showSkillsModal}
          setShowModal={setShowSkillsModal}
          roadmapDetails={{
            project_id: currentEntityId,
            project_name:
              currentEntity === ENTITY.ROADMAP ? roadmapName : projectName,
            durations: projectDurations,
            duration_with_delay: duration_with_delay,
            duration: projectDuration,
            start_date: projectStart,
            end_date: projectEnd,
            skills_required,
            skill_members,
            is_sim_pending,
            skill_capacity_percent,
            members_data: projectMembersData,
          }}
          sim_flag={sim_flag}
          handleRefreshClick={handleRefreshClick}
          setSimulationToggle={handleSimulationToggle}
          readOnly={readOnly || !!template_id}
        />
      )}

      {/* Project History Modal */}
      {showProjectHistoryModal && currentEntityId && (
        <ViewHistoryModal
          showViewHistoryModal={showProjectHistoryModal}
          closeViewHistoryModal={() => setShowProjectHistoryModal(false)}
          projectId={currentEntityId}
        />
      )}

      <CreateTaskWarningModal
        showCreateTaskWarningModal={showCreateTaskWarning}
        setShowCreateTaskWarningModal={setShowCreateTaskWarning}
        onCreateTaskConfirm={handleCreateTaskConfirm}
      />

      <MergeWarningModal
        showMergeWarningModal={showMergeWarningModal}
        setShowMergeWarningModal={setShowMergeWarningModal}
        onRefresh={handleRefreshClick}
      />

      <ResetWarningModal
        showResetWarningModal={showResetWarningModal}
        setShowResetWarningModal={setShowResetWarningModal}
        onRefresh={handleRefreshClick}
      />

      <VersionHistoryModal
        showVersionHistoryModal={showVersionHistoryModal}
        setShowVersionHistoryModal={setShowVersionHistoryModal}
      />

      {/* Email Notification Modal */}
      {showEmailNotificationModal && currentEntityId && (
        <EmailNotificationModal
          showEmailNotificationModal={showEmailNotificationModal}
          closeEmailNotificationModal={() =>
            setShowEmailNotificationModal(false)
          }
          projectId={currentEntityId}
        />
      )}

      <SyncModal
        showSyncModal={showSyncModal}
        setShowSyncModal={setShowSyncModal}
      />

      <ShareModal
        showShareModal={showShareModal}
        onCloseShareModal={() => setShowShareModal(false)}
      />
    </div>
  );
};

export default DashboardDetails;
